import { useState } from 'react';
import { CRM_LEAD_REJECT_REASONS, type CrmLeadRejectReason } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { t } from '@/i18n';

const NOTE_MAX = 500;

interface Props {
  open: boolean;
  /** How many leads the confirmation applies to (1 = single row / sheet). */
  count: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reasonCode: CrmLeadRejectReason, note: string) => Promise<void>;
}

/**
 * Hiding a lead is a status change with a REASON: the queue shows the code
 * next to the badge, so "why did this disappear" is answerable months later
 * without opening the row. The free-text note stays optional.
 *
 * The dialog holds no reset logic: the queue gives it a `key` derived from the
 * ids being hidden, so every fresh open is a remount with empty state and a
 * reason picked for one lead can never leak into the next.
 */
export function HideLeadDialog({ open, count, onOpenChange, onSubmit }: Props) {
  const [reason, setReason] = useState<CrmLeadRejectReason | ''>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(reason, note.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {count > 1
              ? t('crm.leads.hideDialog.titleMany', { count: String(count) })
              : t('crm.leads.hideDialog.title')}
          </DialogTitle>
          <DialogDescription>{t('crm.leads.hideDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('crm.leads.hideDialog.reason')}</Label>
            <RadioGroup value={reason} onValueChange={(value) => setReason(value as CrmLeadRejectReason)}>
              {CRM_LEAD_REJECT_REASONS.map((code) => (
                <label key={code} className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value={code} id={`hide-reason-${code}`} />
                  {t(`crm.leadRejectReason.${code}`)}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hide-lead-note">{t('crm.leads.hideDialog.note')}</Label>
            <Textarea
              id="hide-lead-note"
              value={note}
              maxLength={NOTE_MAX}
              rows={3}
              placeholder={t('crm.leads.hideDialog.notePlaceholder')}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={submitting} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!reason || submitting} onClick={handleSubmit}>
            {t('crm.leads.hideDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
